import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Coins, Loader2, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Currency preference card — fetches live FX rates from the European Central Bank
 * (via Frankfurter), persists the user's choice, and shows a quick conversion
 * preview ($10,000 → chosen currency) so users see the rate they'll actually get.
 */
const CurrencyPreferenceCard = () => {
  const [rates, setRates] = useState(null);
  const [labels, setLabels] = useState({});
  const [supported, setSupported] = useState([]);
  const [asOf, setAsOf] = useState('');
  const [source, setSource] = useState('');
  const [current, setCurrent] = useState('USD');
  const [pending, setPending] = useState('USD');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ratesRes, prefRes] = await Promise.all([
          axios.get(`${API_URL}/currency/rates`),
          axios.get(`${API_URL}/currency/preference`, { headers }).catch(() => ({ data: { currency: 'USD' } })),
        ]);
        if (cancelled) return;
        setRates(ratesRes.data.rates || {});
        setLabels(ratesRes.data.labels || {});
        setSupported(ratesRes.data.supported || []);
        setAsOf(ratesRes.data.as_of || '');
        setSource(ratesRes.data.source || '');
        const pref = (prefRes.data?.currency || 'USD').toUpperCase();
        setCurrent(pref);
        setPending(pref);
      } catch (err) {
        console.error('Failed to load currency data', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (pending === current) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/currency/preference`, { currency: pending }, { headers });
      setCurrent(pending);
      toast.success(`Display currency set to ${pending}`);
      // Inform the rest of the app so other widgets can re-render
      window.dispatchEvent(new CustomEvent('cartie:currency-changed', { detail: { currency: pending } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save currency');
    } finally {
      setSaving(false);
    }
  };

  const previewAmount = 10000; // demo: convert 10k USD into chosen currency
  let preview = null;
  if (rates && rates[pending] != null) {
    const value = previewAmount * rates[pending];
    preview = new Intl.NumberFormat(undefined, { style: 'currency', currency: pending, maximumFractionDigits: pending === 'JPY' ? 0 : 2 }).format(value);
  }

  return (
    <Card data-testid="currency-preference-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-500" />
              Display Currency
            </CardTitle>
            <CardDescription>
              Show all costs in your preferred currency. Rates auto-refresh daily from the European Central Bank.
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-[11px] font-bold tracking-widest">
            {source?.toLowerCase().includes('frankfurter') ? 'LIVE · ECB' : 'CACHED'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading rates…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="md:col-span-1 space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Currency</label>
                <Select value={pending} onValueChange={setPending}>
                  <SelectTrigger data-testid="currency-select-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {supported.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`currency-option-${c}`}>{labels[c] || c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-1 space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Live preview</label>
                <div className="px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm">
                  <span className="text-slate-400">$10,000 USD =</span>{' '}
                  <span className="font-bold text-emerald-600 dark:text-emerald-400" data-testid="currency-preview">{preview || '—'}</span>
                </div>
              </div>

              <div className="md:col-span-1 flex md:justify-end">
                <Button
                  onClick={save}
                  disabled={saving || pending === current}
                  className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-700"
                  data-testid="currency-save-btn"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving…</>
                  ) : pending === current ? (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Saved</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Apply</>
                  )}
                </Button>
              </div>
            </div>

            <div className="text-xs text-slate-400 flex flex-wrap items-center gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
              <span>Active: <span className="font-mono font-semibold text-slate-400 dark:text-slate-300">{current}</span></span>
              <span>Rates as of: <span className="font-mono">{asOf}</span></span>
              <span>Source: <span className="font-medium">{source}</span></span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CurrencyPreferenceCard;
